/**
 * StatusIndicator.tsx — Carbon ShapeIndicator-anatomi
 * (preview-statusindicators-preview-shapeindicator)
 *
 * Replikert i native CSS Modules med --cds-* tokens.
 *
 * Carbon-standard som følges:
 * - 11 offisielle kind-verdier (failed, critical, high, medium, low, cautious,
 *   undefined, stable, informative, incomplete, draft)
 * - Hver kind har et ikon fra @carbon/icons-react + en support-farge
 * - Størrelse: 16px ikon, label ved siden av (body-compact-01, text-primary)
 * - Square layout, inline-flex
 *
 * Kilde: https://github.com/carbon-design-system/carbon/blob/main/packages/react/src/components/ShapeIndicator/index.tsx
 *
 * Bruk:
 *   <StatusIndicator kind="failed" label="Kritisk" />
 *   <StatusIndicator kind="stable" label="Stabil" />
 */

import {
  Critical,
  CriticalSeverity,
  DiamondFill,
  LowSeverity,
  Caution,
  CircleFill,
  CircleStroke,
} from '@carbon/icons-react';
import styles from './status-indicator.module.css';

/**
 * Offisielle Carbon ShapeIndicator-kind-verdier.
 * Kilde: https://react.carbondesignsystem.com/?path=/story/preview-statusindicators-preview-shapeindicator--default
 */
export type StatusIndicatorKind =
  | 'failed'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'cautious'
  | 'undefined'
  | 'stable'
  | 'informative'
  | 'incomplete'
  | 'draft';

interface StatusIndicatorProps {
  kind: StatusIndicatorKind;
  /** Vist tekst ved siden av ikonet */
  label: string;
  /** Valgfri ekstra klasse */
  className?: string;
}

// Custom incomplete-ikon (hvit sirkel med sort halvdel) — replikert fra Carbon-kilden
const IncompleteIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={16}
    height={16}
    fill="none"
    aria-hidden="true"
    {...props}
  >
    <path
      fill="#fff"
      fillOpacity={0.01}
      d="M0 0h16v16H0z"
      style={{ mixBlendMode: 'multiply' }}
    />
    <path
      style={{ fill: 'var(--cds-icon-primary, #161616)' }}
      d="M8 2a6 6 0 1 0 0 12A6 6 0 0 0 8 2Zm0 2a4.004 4.004 0 0 1 4 4H4a4.004 4.004 0 0 1 4-4Z"
    />
  </svg>
);

// Ikon-mapping per Carbon-kind (kopiert fra Carbon-kilden)
const SHAPE_MAP: Record<StatusIndicatorKind, React.ComponentType<{ size?: number }>> = {
  failed: Critical,
  critical: CriticalSeverity,
  high: Caution,
  medium: DiamondFill,
  low: LowSeverity,
  cautious: Caution,
  undefined: DiamondFill,
  stable: CircleFill,
  informative: LowSeverity,
  incomplete: IncompleteIcon as unknown as React.ComponentType<{ size?: number }>,
  draft: CircleStroke,
};

export default function StatusIndicator({
  kind,
  label,
  className,
}: StatusIndicatorProps) {
  const Shape = SHAPE_MAP[kind] ?? DiamondFill;
  const kindClass = styles[kind] || styles.undefined;

  return (
    <span className={`${styles.indicator} ${kindClass}${className ? ` ${className}` : ''}`}>
      <Shape size={16} aria-hidden="true" />
      <span className={styles.label}>{label}</span>
    </span>
  );
}
